import { useEffect, useState } from 'react'

import classNames from 'classnames'
import Navigation from 'employeeDashboard/common/navigation/Navigation'

import { FamilyMemberInsurancePolicy, PlanPeriodDto, benefitPlanPeriods, getFamilyMemberInsurancePolicies } from 'core'
import { uniqBy } from 'core/application/commons/arrayUtils'

import NoData from 'common/components/feedback/NoData'

import { Button, Card, Col, Dropdown, Icon, Menu, MenuItem, Row, Skeleton } from 'common/components'
import { formatYearV2 } from 'common/dateFormatters'
import { useQueryRequestV2 } from 'common/hooks'
import { ROUTE_PATHS } from 'common/routePaths'

import DependantCollapsible from './DependentCollapsible'
import RemoveDependantModal from './RemoveDependentModal'
import SaveDependentModal from './SaveDependentModal'

export type Dependent = {
  firstName: string
  lastName: string
  policyName: string
  startDate: Date | null
  endDate: Date | null
}

export type DependentToEdit = Dependent & { id: string }

const DependentInformation = () => {
  const [addDependentIsVisible, setAddDependentIsVisible] = useState(false)
  const [dependentToEdit, setDependentToEdit] = useState<DependentToEdit | null>(null)
  const [dependentToRemoveId, setDependentToRemoveId] = useState<string | null>(null)
  const [selectedPlanPeriodYear, setSelectedPlanPeriodYear] = useState<string | null>(null)

  const { data: planPeriods } = useQueryRequestV2<PlanPeriodDto[]>({
    initialState: null,
    queryFn: () => benefitPlanPeriods(),
  })

  const {
    data: familyMemberInsurancePolicies,
    isLoading: insurancePoliciesQueryIsLoading,
    reload,
    sendQuery: sendFamilyMemberInsurancePoliciesQuery,
  } = useQueryRequestV2<FamilyMemberInsurancePolicy[]>({
    initialState: [],
    queryFn: () => getFamilyMemberInsurancePolicies(selectedPlanPeriodYear),
    enable: !!selectedPlanPeriodYear,
  })

  useEffect(() => {
    if (planPeriods && planPeriods.length > 0) {
      setSelectedPlanPeriodYear(formatYearV2(planPeriods?.find((x) => x.active)?.startDate) ?? '')
    }
  }, [planPeriods])

  const insurancePoliciesForMember = (familyMemberId: string) =>
    familyMemberInsurancePolicies.filter((x) => x.familyMemberId === familyMemberId)

  const uniqueFamilyMemberInsurancePolicies = uniqBy(familyMemberInsurancePolicies, 'familyMemberId')

  return (
    <>
      <Navigation
        title={
          <div className="flex flex__align_center w-100 flex__space_between pl-8">
            <h5 className="mb-0">Dependent information</h5>
            <div className="flex">
              {selectedPlanPeriodYear && (
                <Dropdown
                  overlay={
                    <Menu
                      onClick={(item) => {
                        setSelectedPlanPeriodYear(item.key)
                        sendFamilyMemberInsurancePoliciesQuery(() => getFamilyMemberInsurancePolicies(item.key))
                      }}
                    >
                      {planPeriods.map((item) => {
                        const year = formatYearV2(item.startDate)
                        return (
                          <MenuItem key={year}>
                            <span>{year}</span>
                          </MenuItem>
                        )
                      })}
                    </Menu>
                  }
                  trigger={['click']}
                  placement="bottomLeft"
                >
                  <Button className="ant-dropdown-link grey-link ml-8 dropdown-btn" onClick={(e) => e.preventDefault()}>
                    {selectedPlanPeriodYear}
                    <Icon type="far fa-angle-down" className="ml-4" fontAwesome />
                  </Button>
                </Dropdown>
              )}
            </div>
          </div>
        }
        redirectPath={ROUTE_PATHS.ELIGIBLE_EMPLOYEE_DASHBOARD}
      >
        <Card className="bg-gray-3">
          <Row>
            <Col span={9}>
              <h6>
                <Icon fontAwesome type="far fa-users" /> Covered Dependents
              </h6>
            </Col>
            <Col span={15}>
              <Skeleton active loading={insurancePoliciesQueryIsLoading}>
                {uniqueFamilyMemberInsurancePolicies.length === 0 ? (
                  <NoData description="You have no covered dependents for this benefit year." />
                ) : (
                  uniqueFamilyMemberInsurancePolicies.map((insurancePolicy) => (
                    <DependantCollapsible
                      key={insurancePolicy.id}
                      firstName={insurancePolicy.firstName}
                      lastName={insurancePolicy.lastName}
                      isCoveredForTheFullYear={insurancePolicy.isCoveredForTheFullYear}
                      familyMemberInsurancePolicies={insurancePoliciesForMember(insurancePolicy.familyMemberId)}
                      hasMultiplePolicies={insurancePoliciesForMember(insurancePolicy.familyMemberId).length > 1}
                      onEdit={(policy: DependentToEdit) => {
                        setDependentToEdit(policy)
                        setAddDependentIsVisible(true)
                      }}
                      onRemove={(id: string) => setDependentToRemoveId(id)}
                    />
                  ))
                )}
              </Skeleton>
              <div
                className={classNames(
                  'w-100 flex mt-16',
                  uniqueFamilyMemberInsurancePolicies.length === 0 && 'flex__center',
                )}
              >
                <Button
                  type="primary"
                  onClick={() => {
                    setDependentToEdit(null)
                    setAddDependentIsVisible(true)
                  }}
                >
                  <Icon fontAwesome className="fa fa-plus mr-8" />
                  Add dependent
                </Button>
              </div>
            </Col>
          </Row>
        </Card>
      </Navigation>

      {addDependentIsVisible && (
        <SaveDependentModal
          visible={addDependentIsVisible}
          initialValues={dependentToEdit}
          onCancel={() => setAddDependentIsVisible(false)}
          onSubmit={() => reload()}
        />
      )}

      <RemoveDependantModal
        visible={dependentToRemoveId !== null}
        dependentPolicyId={dependentToRemoveId ?? ''}
        onCancel={() => setDependentToRemoveId(null)}
        onSubmit={() => reload()}
      />
    </>
  )
}

export default DependentInformation
